import type { CmsFitment, CmsProduct, CmsVariantPrice } from '@/lib/cms';

/** One CMS fitment, flattened for display. Rows are shown exactly as stored - never merged. */
export interface FitmentRow {
  id: number;
  model: string;
  yearStart: number;
  yearEnd: number;
  engine: string;
  fuelSystem: string;
  trim: string;
  partNum: string;
}

export interface YearRange {
  start: number;
  end: number;
}

export interface PartSummary {
  partNum: string;
  name: string;
  kind: 'filter' | 'kit' | 'care';
  href: string;
  imageUrl: string | null;
  buyButtonId: string | null;
  price: number | null;
  compareAtPrice: number | null;
  /** Years covered per model. Only overlapping/adjacent ranges are joined, so gaps stay visible. */
  fits: { model: string; ranges: YearRange[]; engines: string[] }[];
  /** Care products only: this vehicle's filters the CMS links the product to. */
  worksWith: string[];
}

export interface VehicleLandingData {
  rows: FitmentRow[];
  models: string[];
  parts: PartSummary[];
  care: PartSummary[];
  yearMin: number;
  yearMax: number;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Normalises only the base-model part of the name, so "CORVETTE ZR1" shows as "Corvette ZR1";
// the rest ("Z06", "GT Bullitt", "& GT") is kept exactly as stored in the CMS.
export function displayModelName(name: string, baseModel: string): string {
  const rest = name.trim().replace(new RegExp(`^${escapeRegExp(baseModel)}\\s*`, 'i'), '');
  return rest ? `${baseModel} ${rest}` : baseModel;
}

export function formatFuelSystem(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}

function joinRanges(ranges: YearRange[]): YearRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const out: YearRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start <= last.end + 1) last.end = Math.max(last.end, r.end);
    else out.push({ ...r });
  }
  return out;
}

// "Part# 2000 Recharge Oil & Cleaner Kit (Green)" -> "Recharge Oil & Cleaner Kit (Green)"
export function productTitle(name: string, partNum: string): string {
  return name.replace(new RegExp(`^\\s*(green filter\\s*-?\\s*)?part\\s*#\\s*${partNum}\\s*`, 'i'), '').trim() || name;
}

const isThrottleKit = (p: CmsProduct) => /throttle/i.test(p.productType?.name ?? '');

/**
 * Care products (cleaner, recharge oil) linked to the given air filters, with the filters each one is
 * linked to. Only filters' links count (a Pedal Power Kit links to its filter, which is not a care
 * product), and anything that is itself one of this vehicle's parts is skipped. Unpublished links are ids and are skipped.
 */
export function relatedCare(fitments: CmsFitment[]): { product: CmsProduct; worksWith: string[] }[] {
  const vehicleParts = new Set(fitments.map((f) => f.product?.gfuPartNum).filter(Boolean));
  const byPart = new Map<string, { product: CmsProduct; worksWith: Set<string> }>();
  fitments.forEach((f) => {
    const filter = f.product;
    if (!filter || isThrottleKit(filter)) return;
    (filter.relatedProducts ?? []).forEach((rel) => {
      if (typeof rel !== 'object' || !rel?.gfuPartNum || vehicleParts.has(rel.gfuPartNum)) return;
      const entry = byPart.get(rel.gfuPartNum) ?? { product: rel, worksWith: new Set<string>() };
      entry.worksWith.add(filter.gfuPartNum);
      byPart.set(rel.gfuPartNum, entry);
    });
  });
  return [...byPart.values()].map(({ product, worksWith }) => ({ product, worksWith: [...worksWith].sort() }));
}

export function formatRange({ start, end }: YearRange): string {
  return start === end ? String(start) : `${start}–${end}`;
}

function cheapestPrice(prices: CmsVariantPrice[], productId: number) {
  return prices.filter((v) => v.product === productId && typeof v.price === 'number').sort((a, b) => a.price - b.price)[0];
}

export function buildVehicleLandingData(fitments: CmsFitment[], prices: CmsVariantPrice[], baseModel: string): VehicleLandingData {
  const rows: FitmentRow[] = fitments
    .map((f) => ({
      id: f.id,
      model: displayModelName(f.vehicleModel.name, baseModel),
      yearStart: f.yearStart,
      yearEnd: f.yearEnd,
      engine: f.engine?.trim() ?? '',
      fuelSystem: formatFuelSystem(f.fuelSystem ?? ''),
      trim: f.trim?.trim() ?? '',
      partNum: f.product!.gfuPartNum,
    }))
    .sort((a, b) => a.yearStart - b.yearStart || a.yearEnd - b.yearEnd || a.engine.localeCompare(b.engine) || a.partNum.localeCompare(b.partNum));

  // Base model first, then the variants with the most fitments (Mustang, Mustang GT, Mustang Bullitt, ...).
  const fitmentCount = (model: string) => rows.filter((r) => r.model === model).length;
  const models = [...new Set(rows.map((r) => r.model))].sort(
    (a, b) => Number(b === baseModel) - Number(a === baseModel) || fitmentCount(b) - fitmentCount(a) || a.localeCompare(b),
  );

  const products = new Map<string, NonNullable<CmsFitment['product']>>();
  fitments.forEach((f) => f.product && products.set(f.product.gfuPartNum, f.product));

  const parts: PartSummary[] = [...products.values()].map((p) => {
    const own = rows.filter((r) => r.partNum === p.gfuPartNum);
    const cheapest = cheapestPrice(prices, p.id);
    return {
      partNum: p.gfuPartNum,
      name: p.name,
      // Throttle controllers (Pedal Power Kits) are an upgrade, not an air filter.
      kind: isThrottleKit(p) ? 'kit' : 'filter',
      href: `/store/filter/${p.gfuPartNum}`,
      imageUrl: p.mainImageUrl ?? null,
      buyButtonId: p.buyUrl ?? null,
      price: cheapest?.price ?? null,
      compareAtPrice: cheapest?.compareAtPrice ?? null,
      fits: models
        .map((model) => {
          const mine = own.filter((r) => r.model === model);
          return {
            model,
            ranges: joinRanges(mine.map((r) => ({ start: r.yearStart, end: r.yearEnd }))),
            engines: [...new Set(mine.map((r) => r.engine).filter(Boolean))],
          };
        })
        .filter((fit) => fit.ranges.length),
      worksWith: [],
    };
  });

  // Newest vehicles first, so the most-searched filters lead.
  const latestYear = (p: PartSummary) => Math.max(...p.fits.flatMap((f) => f.ranges.map((r) => r.end)));
  parts.sort((a, b) => latestYear(b) - latestYear(a) || a.partNum.localeCompare(b.partNum));

  // Kits first (the complete clean-and-oil set), then the products that suit the most of this vehicle's filters.
  const care: PartSummary[] = relatedCare(fitments)
    .map(({ product, worksWith }) => {
      const cheapest = cheapestPrice(prices, product.id);
      return {
        partNum: product.gfuPartNum,
        name: productTitle(product.name, product.gfuPartNum),
        kind: 'care' as const,
        href: `/store/filter/${product.gfuPartNum}`,
        imageUrl: product.mainImageUrl ?? null,
        buyButtonId: product.buyUrl ?? null,
        price: cheapest?.price ?? null,
        compareAtPrice: cheapest?.compareAtPrice ?? null,
        fits: [],
        worksWith,
      };
    })
    .sort((a, b) => Number(/kit/i.test(b.name)) - Number(/kit/i.test(a.name)) || b.worksWith.length - a.worksWith.length || a.partNum.localeCompare(b.partNum));

  return {
    rows,
    models,
    parts,
    care,
    yearMin: rows.length ? Math.min(...rows.map((r) => r.yearStart)) : 0,
    yearMax: rows.length ? Math.max(...rows.map((r) => r.yearEnd)) : 0,
  };
}
